/**
 * 桌面端按钮行：lg+ 时若挤占则隐藏音量滑条，有空间时恢复
 * 抽离自 MusicPlayer.js
 */
import { useEffect } from 'react';

/**
 * @param {object} options
 * @param {React.RefObject<HTMLElement|null>} options.desktopControlsRowRef
 * @param {React.RefObject<HTMLElement|null>} options.volumeSliderWrapRef
 * @param {React.MutableRefObject<number>} options.volumeSliderWidthRef
 * @param {boolean} options.isLgUp
 * @param {boolean} options.hideVolumeSlider
 * @param {function(boolean): void} options.setHideVolumeSlider
 * @param {unknown[]} options.deps - 触发重新测量的依赖（如 currentMusic?.id, showLyrics, showPlaylist 等）
 */
export function useDesktopControlsOverflow({
  desktopControlsRowRef,
  volumeSliderWrapRef,
  volumeSliderWidthRef,
  isLgUp,
  hideVolumeSlider,
  setHideVolumeSlider,
  deps = []
}) {
  // eslint warning fix：dependency array 不使用 spread（...deps）。
  // 将 deps 归一化为稳定的 key，只在关键值变化时触发重新测量。
  const depsKey = (() => {
    try {
      const arr = Array.isArray(deps) ? deps : [];
      if (arr.length === 0) return '';
      return arr.map((v) => {
        if (v === null) return 'null';
        const t = typeof v;
        if (t === 'string') return `s:${v}`;
        if (t === 'number') return `n:${Number.isFinite(v) ? v : 'NaN'}`;
        if (t === 'boolean') return `b:${v ? 1 : 0}`;
        if (t === 'undefined') return 'u';
        if (t === 'function') return 'fn';
        // object / symbol / bigint：尽量转为可比较字符串（避免循环引用）
        try { return `o:${String(v)}`; } catch (_) { return 'o'; }
      }).join('|');
    } catch (_) {
      return '';
    }
  })();

  useEffect(() => {
    const row = desktopControlsRowRef.current;
    if (!row) return;

    if (!isLgUp) return;

    let rafId = 0;

    const measure = () => {
      const sliderWrap = volumeSliderWrapRef.current;
      if (sliderWrap && sliderWrap.offsetParent !== null) {
        const w = sliderWrap.getBoundingClientRect().width || 0;
        if (w > 0) volumeSliderWidthRef.current = w;
      }

      const overflow = row.scrollWidth > (row.clientWidth + 1);

      if (!hideVolumeSlider && overflow) {
        setHideVolumeSlider(true);
        return;
      }

      if (hideVolumeSlider) {
        const free = row.clientWidth - row.scrollWidth;
        const need = (volumeSliderWidthRef.current || 140) + 12;
        if (free > need) {
          setHideVolumeSlider(false);
        }
      }
    };

    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        measure();
      });
    };

    schedule();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => schedule());
      try { ro.observe(row); } catch (_) {}
    }
    window.addEventListener('resize', schedule);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
      if (ro) {
        try { ro.disconnect(); } catch (_) {}
      }
    };
  }, [hideVolumeSlider, isLgUp, desktopControlsRowRef, volumeSliderWrapRef, volumeSliderWidthRef, setHideVolumeSlider, depsKey]);
}
